import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AUTH_PROVIDERS, isFigmaEmbedAllowedOrigin } from '@/consts/auth.consts';
import { FIGMA_EMBED_MESSAGE_TYPES, WEAVE_EMBED_BOOT_AS_VIEWER_KEY } from '@/consts/figma-embed-messages';
import { postReloadingToParent } from '@/utils/figma-embed-messages.utils';
import { AuthContext } from '@/contexts/AuthContext';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { useGlobalStore } from '@/state/global.state';

/**
 * Embed-mode session state: whether we're inside Figma's embed iframe, plus
 * the permission flags and multiplayer presence the parent pushes over
 * postMessage. Figma owns these values; we mirror locally. `null` on any
 * field means "not yet received from parent" — treat as still-loading
 * rather than default-allow / empty.
 *
 * `presenceUsers` is capped by Figma at `MAX_AVATARS_SHOWN_UI3` to bound the
 * postMessage payload; use `presenceTotalCount` for the uncapped headcount
 * when rendering a `+N` overflow indicator.
 */
export interface FigmaEmbedPresenceUser {
  sessionID: number;
  userID: string;
  name: string;
  imageURL: string;
  color: string;
}

/**
 * `Add to sidebar ▶` state. `null` when not loaded or the user can't
 * favorite this file (Figma collapses `canUserHaveFavorites` into null).
 */
export interface FigmaEmbedFileMenuSidebar {
  isFavorited: boolean;
  /** null when not favorited; otherwise the section id (could be the Starred section's id). */
  favoriteSectionId: string | null;
  /** Section name; '' for the default Starred section — render the localized "Starred" fallback. */
  favoriteSectionName: string | null;
  /** Ordered + filtered sections from canonical `getOrderedFilteredSections`. Includes the Starred section (name === ''). */
  sections: { id: string; name: string }[];
}

/**
 * Filename-dropdown disabled-state derivations pushed from Figma:
 *   - `Go to project` is hidden when `hasNonDraftsProject` is false
 *   - `Move file…` is disabled when `canMove` is false; shows
 *     `moveFileSideText` (e.g. "Request" / "Request sent") as side-text
 *   - `Move to trash` is disabled when `canTrash` is false
 *   - `Pin to workspace` is hidden when `canPinFileToWorkspace` is false
 *     (matches canonical visibility rule)
 *   - AI balance entry is hidden when `hideAiBalance` is true because Figma owns the credit currency in the embed
 */
export interface FigmaEmbedFileMenuState {
  canMove: boolean;
  moveFileSideText: string | null;
  canTrash: boolean;
  hasNonDraftsProject: boolean;
  canPinFileToWorkspace: boolean;
  sidebar: FigmaEmbedFileMenuSidebar | null;
  hideAiBalance: boolean;
}

/**
 * Which shell the Weave iframe is running inside. Carried over the
 * `WEAVE_SHELL_STATE` postMessage. Enum (not boolean) so we can grow the
 * vocabulary without a wire-format break — e.g. add `'mobile_web'` later
 * if/when surfaces differ there.
 */
export type WeaveShellType = 'desktop' | 'web';

/**
 * Edit-lock authority for the current user, pushed by Figma over `ROLE_CHANGE`:
 *   - `'editor'` — holds the lock, full editing
 *   - `'viewer'` — locked out by another editor, read-only
 * `null` means Figma hasn't pushed a role yet (fall back to the JWT permission).
 */
export type WeaveEmbedEditLockRole = 'editor' | 'viewer';

interface FigmaEmbedModeContextValue {
  isFigmaEmbedMode: boolean;
  canCreateWeaveFile: boolean | null;
  canEditWeaveFile: boolean | null;
  // Whether the user can duplicate ("Make a copy…") the open file. Distinct from
  // canCreateWeaveFile: copying the current file requires export permission on it
  // (e.g. the org "prevent exporting by guest viewers" setting can deny it) while
  // creating a blank Weave only needs create permission. `null` until the parent
  // replies — treat as not-allowed.
  canDuplicateFile: boolean | null;
  /**
   * Whether Figma reports the current user has AI credits available to run
   * (from Figma's own AI meter for `AI_ACTIONS.WEAVE`). `null` until the
   * parent replies. Only meaningful when the file charges real Figma credits
   * (the `weave-file-figma-credits-consumption` flag); callers gate on that.
   */
  figmaAiMeterHasCredits: boolean | null;
  figmaAiMeterResetsAt: string | undefined;
  presenceUsers: FigmaEmbedPresenceUser[] | null;
  presenceTotalCount: number | null;
  fileMenuState: FigmaEmbedFileMenuState | null;
  shellType: WeaveShellType | null;
  editLockRole: WeaveEmbedEditLockRole | null;
  /** Figma parent origin (from the first validated postMessage); `null` until then. Used to build env-correct links to Figma pages. */
  figmaParentOrigin: string | null;
  /** Logged-out viewer of an "Anyone with the link" Weave file (Figma-owned). */
  isAnonymousViewer: boolean;
}

const DEFAULT: FigmaEmbedModeContextValue = {
  isFigmaEmbedMode: false,
  canCreateWeaveFile: null,
  canEditWeaveFile: null,
  canDuplicateFile: null,
  figmaAiMeterHasCredits: null,
  figmaAiMeterResetsAt: undefined,
  presenceUsers: null,
  presenceTotalCount: null,
  fileMenuState: null,
  shellType: null,
  editLockRole: null,
  figmaParentOrigin: null,
  isAnonymousViewer: false,
};

function isWeaveShellType(value: unknown): value is WeaveShellType {
  return value === 'desktop' || value === 'web';
}

function isWeaveEmbedEditLockRole(value: unknown): value is WeaveEmbedEditLockRole {
  return value === 'editor' || value === 'viewer';
}

const FigmaEmbedModeContext = createContext<FigmaEmbedModeContextValue>(DEFAULT);

function isPresenceUserArray(value: unknown): value is FigmaEmbedPresenceUser[] {
  if (!Array.isArray(value)) return false;
  return value.every(
    (entry) =>
      entry !== null &&
      typeof entry === 'object' &&
      typeof (entry as { sessionID?: unknown }).sessionID === 'number' &&
      typeof (entry as { userID?: unknown }).userID === 'string' &&
      typeof (entry as { name?: unknown }).name === 'string' &&
      typeof (entry as { imageURL?: unknown }).imageURL === 'string' &&
      typeof (entry as { color?: unknown }).color === 'string',
  );
}

function isSidebarPayload(value: unknown): value is FigmaEmbedFileMenuSidebar {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.isFavorited !== 'boolean') return false;
  if (v.favoriteSectionId !== null && typeof v.favoriteSectionId !== 'string') return false;
  if (v.favoriteSectionName !== null && typeof v.favoriteSectionName !== 'string') return false;
  if (!Array.isArray(v.sections)) return false;
  return v.sections.every(
    (entry) =>
      entry !== null &&
      typeof entry === 'object' &&
      typeof (entry as { id?: unknown }).id === 'string' &&
      typeof (entry as { name?: unknown }).name === 'string',
  );
}

export function FigmaEmbedModeProvider({ children }: { children: ReactNode }) {
  const { provider } = useAuthConfig();
  const isFigmaEmbedMode = useMemo(() => provider === AUTH_PROVIDERS.FIGMA_EMBED, [provider]);
  const [canCreateWeaveFile, setCanCreateWeaveFile] = useState<boolean | null>(null);
  const [canEditWeaveFile, setCanEditWeaveFile] = useState<boolean | null>(null);
  const [canDuplicateFile, setCanDuplicateFile] = useState<boolean | null>(null);
  const [figmaAiMeterHasCredits, setFigmaAiMeterHasCredits] = useState<boolean | null>(null);
  const [figmaAiMeterResetsAt, setFigmaAiMeterResetsAt] = useState<string | undefined>(undefined);
  const [presenceUsers, setPresenceUsers] = useState<FigmaEmbedPresenceUser[] | null>(null);
  const [presenceTotalCount, setPresenceTotalCount] = useState<number | null>(null);
  const [fileMenuState, setFileMenuState] = useState<FigmaEmbedFileMenuState | null>(null);
  const [shellType, setShellType] = useState<WeaveShellType | null>(null);
  const [editLockRole, setEditLockRole] = useState<WeaveEmbedEditLockRole | null>(null);
  const [figmaParentOrigin, setFigmaParentOrigin] = useState<string | null>(null);
  // Anonymous-viewer state is derived from the embed token's `anonymous` claim
  // (decoded in the auth provider), not a postMessage from Figma — single source
  // of truth, minted server-side at token time.
  const { isAnonymousViewer } = useContext(AuthContext);
  // Last role we received, to detect transitions in the message handler (whose
  // effect only re-binds on isFigmaEmbedMode, so it can't read live editLockRole).
  const prevEditLockRoleRef = useRef<WeaveEmbedEditLockRole | null>(null);

  useEffect(() => {
    if (!isFigmaEmbedMode) return undefined;

    const handleMessage = (event: MessageEvent) => {
      if (!isFigmaEmbedAllowedOrigin(event.origin)) return;
      // Capture the hosting Figma origin once (see figmaParentOrigin field doc).
      setFigmaParentOrigin((prev) => prev ?? event.origin);
      const data = event.data as {
        type?: unknown;
        canCreateWeaveFile?: unknown;
        canEditWeaveFile?: unknown;
        canDuplicateFile?: unknown;
        hasCredits?: unknown;
        resetsAt?: unknown;
        users?: unknown;
        totalCount?: unknown;
        canMove?: unknown;
        moveFileSideText?: unknown;
        canTrash?: unknown;
        hasNonDraftsProject?: unknown;
        canPinFileToWorkspace?: unknown;
        hideAiBalance?: unknown;
        sidebar?: unknown;
        shellType?: unknown;
        role?: unknown;
        reload?: unknown;
      } | null;
      if (!data || typeof data.type !== 'string') return;
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.PERMISSIONS_REFRESH) {
        if (typeof data.canCreateWeaveFile === 'boolean') {
          setCanCreateWeaveFile(data.canCreateWeaveFile);
        }
        if (typeof data.canEditWeaveFile === 'boolean') {
          setCanEditWeaveFile(data.canEditWeaveFile);
        }
        if (typeof data.canDuplicateFile === 'boolean') {
          setCanDuplicateFile(data.canDuplicateFile);
        }
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.AI_METER_REFRESH) {
        if (typeof data.hasCredits !== 'boolean') return;
        setFigmaAiMeterHasCredits(data.hasCredits);
        if (data.resetsAt !== undefined && typeof data.resetsAt !== 'string') return;
        setFigmaAiMeterResetsAt(data.resetsAt);
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.PRESENCE_REFRESH) {
        if (!isPresenceUserArray(data.users)) return;
        if (typeof data.totalCount !== 'number') return;
        setPresenceUsers(data.users);
        setPresenceTotalCount(data.totalCount);
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.SHELL_STATE) {
        if (!isWeaveShellType(data.shellType)) return;
        setShellType(data.shellType);
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.ROLE_CHANGE) {
        if (!isWeaveEmbedEditLockRole(data.role)) return;
        const prevRole = prevEditLockRoleRef.current;
        prevEditLockRoleRef.current = data.role;
        setEditLockRole(data.role);
        // On takeover (promoted to editor after another user held the lock) the
        // canvas we loaded as a viewer can be stale — reload to start from the
        // current server version (else the first save conflicts and forces a
        // reload anyway, losing local edits). The parent sets `reload` only for
        // that case: not on initial load or a self-reacquire after idle.
        if (data.role === 'editor' && data.reload === true) {
          // Announce the reload first so the parent re-raises its loading overlay
          // before the canvas goes blank (no banner flash over a black reload).
          postReloadingToParent();
          window.location.reload();
        } else if (data.role === 'viewer' && prevRole === 'editor') {
          // Demotion (editor -> viewer): read-only is enforced by rebuilding the
          // FlowGraph, which reseeds from the page-load snapshot and would drop
          // our just-saved edits from view. Reload so we re-fetch and render the
          // current server version read-only. Saved data is safe; this only
          // corrects the view. (A smoother in-place editability toggle that
          // avoids the reload is tracked as a follow-up.)
          // Boot the reloaded page read-only until the role re-syncs, so the JWT
          // null-fallback doesn't briefly re-enable editor UI for a user we just
          // demoted (see WEAVE_EMBED_BOOT_AS_VIEWER_KEY).
          try {
            sessionStorage.setItem(WEAVE_EMBED_BOOT_AS_VIEWER_KEY, '1');
          } catch {
            // sessionStorage can throw (privacy mode / disabled); the role
            // handshake still corrects the view, so this is best-effort.
          }
          // Re-raise the parent loading overlay before the canvas blanks.
          postReloadingToParent();
          window.location.reload();
        }
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.FILE_MENU_STATE) {
        // TODO(weave): revisit if these validations should/can be dropped
        if (typeof data.canMove !== 'boolean') return;
        if (typeof data.canTrash !== 'boolean') return;
        if (typeof data.hasNonDraftsProject !== 'boolean') return;
        if (typeof data.canPinFileToWorkspace !== 'boolean') return;
        if (data.moveFileSideText !== null && typeof data.moveFileSideText !== 'string') return;
        // `sidebar` is missing on FILE_MENU_STATE messages from older Figma
        // deploys — treat undefined as null rather than rejecting the message.
        if (data.sidebar != null && !isSidebarPayload(data.sidebar)) return;

        setFileMenuState({
          canMove: data.canMove,
          moveFileSideText: data.moveFileSideText,
          canTrash: data.canTrash,
          hasNonDraftsProject: data.hasNonDraftsProject,
          canPinFileToWorkspace: data.canPinFileToWorkspace,
          hideAiBalance: data.hideAiBalance === true,
          sidebar: data.sidebar ?? null,
        });
        return;
      }
      if (data.type === FIGMA_EMBED_MESSAGE_TYPES.inbound.OPEN_SHORTCUTS_PANEL) {
        useGlobalStore.getState().setIsShortcutsPanelOpen(true, 'figma_help_widget');
        return;
      }
    };
    window.addEventListener('message', handleMessage);

    // Ask the parent for the current values on mount. The parent also pushes
    // proactively whenever each value changes, so these are just to cover the
    // case where the value resolved on the parent before we mounted.
    if (window.parent !== window) {
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.PERMISSIONS_REQUEST }, '*');
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_METER_REQUEST }, '*');
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.PRESENCE_REQUEST }, '*');
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.FILE_MENU_STATE_REQUEST }, '*');
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.SHELL_STATE_REQUEST }, '*');
      // Re-sync the edit-lock role on every (re)boot so a reloaded iframe doesn't
      // sit on the JWT null-fallback (which would treat a locked-out CAN_EDIT user
      // as an editor). The other fields above are already reload-safe this way.
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.ROLE_REQUEST }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isFigmaEmbedMode]);

  const value = useMemo(
    () => ({
      isFigmaEmbedMode,
      canCreateWeaveFile,
      canEditWeaveFile,
      canDuplicateFile,
      figmaAiMeterHasCredits,
      figmaAiMeterResetsAt,
      presenceUsers,
      presenceTotalCount,
      fileMenuState,
      shellType,
      editLockRole,
      figmaParentOrigin,
      isAnonymousViewer,
    }),
    [
      isFigmaEmbedMode,
      canCreateWeaveFile,
      canEditWeaveFile,
      canDuplicateFile,
      figmaAiMeterHasCredits,
      figmaAiMeterResetsAt,
      presenceUsers,
      presenceTotalCount,
      fileMenuState,
      shellType,
      editLockRole,
      figmaParentOrigin,
      isAnonymousViewer,
    ],
  );

  return <FigmaEmbedModeContext.Provider value={value}>{children}</FigmaEmbedModeContext.Provider>;
}

export function useIsFigmaEmbedMode(): boolean {
  return useContext(FigmaEmbedModeContext).isFigmaEmbedMode;
}

export function useFigmaParentOrigin(): string | null {
  return useContext(FigmaEmbedModeContext).figmaParentOrigin;
}

export function useFigmaEmbedPermissions(): {
  canCreateWeaveFile: boolean | null;
  canEditWeaveFile: boolean | null;
  canDuplicateFile: boolean | null;
} {
  const { canCreateWeaveFile, canEditWeaveFile, canDuplicateFile } = useContext(FigmaEmbedModeContext);
  return { canCreateWeaveFile, canEditWeaveFile, canDuplicateFile };
}

/**
 * Whether Figma reports the current user has AI credits to run. `null` until the
 * parent's `AI_METER_REFRESH` arrives.
 */
export function useFigmaEmbedAiMeterHasCredits(): boolean | null {
  return useContext(FigmaEmbedModeContext).figmaAiMeterHasCredits;
}

export function useFigmaEmbedAiMeterResetsAt(): string | undefined {
  return useContext(FigmaEmbedModeContext).figmaAiMeterResetsAt;
}

export function useFigmaEmbedPresence(): {
  presenceUsers: FigmaEmbedPresenceUser[] | null;
  presenceTotalCount: number | null;
} {
  const { presenceUsers, presenceTotalCount } = useContext(FigmaEmbedModeContext);
  return { presenceUsers, presenceTotalCount };
}

export function useFigmaEmbedFileMenuState(): FigmaEmbedFileMenuState | null {
  return useContext(FigmaEmbedModeContext).fileMenuState;
}

/** True for a logged-out viewer of an "Anyone with the link" Weave file. */
export function useIsFigmaEmbedAnonymousViewer(): boolean {
  return useContext(FigmaEmbedModeContext).isAnonymousViewer;
}

export function useFigmaShellType(): WeaveShellType | null {
  return useContext(FigmaEmbedModeContext).shellType;
}

/**
 * The edit-lock role Figma has pushed for the current user, or `null` before the
 * first `ROLE_CHANGE` arrives. `EmbedWorkflow` combines this with the JWT
 * permission to set the authoritative workflow role.
 */
export function useFigmaEmbedEditLockRole(): WeaveEmbedEditLockRole | null {
  return useContext(FigmaEmbedModeContext).editLockRole;
}

/**
 * Convenience derived hook for the common "are we in the desktop app" branch.
 * Returns `null` until the parent's `SHELL_STATE` handshake resolves — treat
 * null as "not desktop" at the call site (avoid hiding affordances during the
 * pre-handshake window).
 */
export function useIsFigmaDesktopApp(): boolean | null {
  const shellType = useContext(FigmaEmbedModeContext).shellType;
  if (shellType === null) return null;
  return shellType === 'desktop';
}
